<template>
  <div class="brief-view space-y-6">
    <!-- Next Meeting -->
    <div v-if="nextMeetingLine" class="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50/70 border border-blue-200/60">
      <i class="fa-regular fa-calendar-clock text-blue-500 text-sm"></i>
      <p class="text-sm font-medium text-blue-900">{{ nextMeetingLine }}</p>
    </div>

    <!-- Summary Banner -->
    <div v-if="header.summary_banner" class="p-3 rounded-lg bg-amber-50/70 border border-amber-200/60">
      <p class="sensitive text-sm text-gray-800 leading-relaxed" data-demo-blur>{{ header.summary_banner }}</p>
    </div>

    <!-- Disruptions Banner -->
    <div v-if="recentDisruptions.length" class="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-amber-50/80 border border-amber-200/60">
      <i class="fa-regular fa-triangle-exclamation text-amber-600 text-sm mt-0.5"></i>
      <div>
        <p class="text-sm font-medium text-amber-900">{{ disruptionsSummary }}</p>
        <p class="text-xs text-amber-700/80 mt-0.5">{{ disruptionsDetail }}</p>
      </div>
    </div>

    <!-- PRIMARY: Suggested Talking Points (hero section) -->
    <section class="rounded-lg border border-orange-200 bg-white">
      <header class="flex items-center justify-between px-4 py-3 border-b border-orange-100 bg-orange-50/50">
        <div class="flex items-center gap-2">
          <span class="w-2 h-2 rounded-full bg-orange-500"></span>
          <h2 class="text-sm font-semibold text-gray-900">Suggested Talking Points</h2>
        </div>
        <span v-if="talkingPoints.length" class="text-xs text-gray-400">{{ talkingPoints.length }} topics</span>
      </header>
      <div class="p-4">
        <div v-if="talkingPoints.length === 0" class="text-sm text-gray-400 py-6 text-center">No talking points generated.</div>
        <div v-else class="space-y-3">
          <div v-for="tp in talkingPoints" :key="tp.rank" class="rounded border border-gray-100 bg-white">
            <div class="p-3 flex items-start gap-3">
              <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-700 text-sm font-bold shrink-0">
                {{ tp.rank }}
              </span>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900">{{ tp.title }}</p>
              </div>
              <button
                class="shrink-0 w-7 h-7 flex items-center justify-center rounded-md border transition-colors"
                :class="isInAgenda('talking_point', tp.rank)
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-600 cursor-default'
                  : 'border-gray-200 text-gray-400 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50'"
                :title="isInAgenda('talking_point', tp.rank) ? 'Added to agenda' : 'Add to agenda'"
                @click.stop="addToAgenda('talking_point', tp.rank, tp.title)"
              >
                <font-awesome-icon :icon="['fas', isInAgenda('talking_point', tp.rank) ? 'check' : 'plus']" class="w-3 h-3" />
              </button>
            </div>
            <div class="px-3 pb-3 pl-14">
              <p v-if="tp.body" class="text-xs text-gray-700 leading-relaxed">{{ tp.body }}</p>
              <div class="flex items-center gap-2 mt-1.5">
                <span v-if="tp.source_chip" class="px-1.5 py-0.5 rounded text-[10px] font-medium" :class="sourceChipClass(tp.source_kind)">
                  {{ tp.source_chip }}
                </span>
                <button v-if="hasTalkingPointDetail(tp)" class="text-xs text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline" @click="toggle('tp', tp.rank)">
                  → {{ isExpanded('tp', tp.rank) ? 'Hide source' : 'View source' }}
                </button>
              </div>
            </div>
            <div v-if="isExpanded('tp', tp.rank) && hasTalkingPointDetail(tp)" class="border-t border-gray-100 bg-gray-50 p-3 space-y-2">
              <div v-for="(src, i) in talkingPointSources(tp)" :key="i" class="text-xs">
                <p class="text-[10px] uppercase tracking-wide text-gray-400 font-medium">{{ src.kind === 'action' ? 'Action item' : 'Blocker' }}</p>
                <p class="font-medium text-gray-900 mt-0.5">{{ src.title }}</p>
                <p v-if="src.description" class="text-gray-700 mt-0.5">{{ src.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECONDARY: Two-column — What's Happened Since + Blockers -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

      <!-- What's Happened Since -->
      <section class="rounded-lg border border-gray-200 bg-white">
        <header class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div class="flex items-center gap-2">
            <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
            <h2 class="text-sm font-semibold text-gray-900">What's Happened Since</h2>
          </div>
          <span v-if="sinceRangeLabel" class="text-xs text-gray-400">{{ sinceRangeLabel }}</span>
        </header>
        <div class="p-4">
          <div v-if="sinceItems.length === 0" class="text-sm text-gray-400 py-6 text-center">No notable changes since the last meeting.</div>
          <div v-else class="space-y-1.5">
            <div v-for="(row, i) in sinceItems" :key="i" class="rounded">
              <div class="flex items-center gap-3 p-2 hover:bg-gray-50 rounded transition-colors cursor-pointer" @click="toggle('since', i)">
                <span class="inline-flex items-center justify-center w-7 h-7 rounded shrink-0" :class="directionBgClass(row.direction)">
                  <span class="text-base" :class="directionTextClass(row.direction)">{{ directionIcon(row.direction) }}</span>
                </span>
                <div class="flex-1 min-w-0">
                  <p class="text-sm text-gray-900 leading-tight">{{ row.label }}</p>
                  <p v-if="row.subtext" class="text-xs text-gray-500 leading-tight">{{ row.subtext }}</p>
                </div>
                <div v-if="row.delta_value || row.trend" class="flex items-center gap-1.5 shrink-0">
                  <span v-if="row.delta_value" class="text-sm font-semibold" :class="directionTextClass(row.direction)">{{ row.delta_value }}</span>
                  <span v-if="row.trend" class="text-xs font-medium" :class="trendClass(row.trend)" :title="'Trend: ' + row.trend">{{ trendIcon(row.trend) }}</span>
                </div>
              </div>
              <div v-if="isExpanded('since', i)" class="ml-10 mb-1 px-3 py-2 bg-gray-50 rounded text-xs text-gray-600">
                <p v-if="row.source"><span class="text-gray-400">Source:</span> {{ row.source }}<span v-if="row.source_id"> · {{ row.source_id }}</span></p>
                <p v-if="row.detail">{{ row.detail }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Blockers (collapsed by default) -->
      <section class="rounded-lg border border-gray-200 bg-white">
        <header
          class="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
          :class="blockersExpanded ? 'border-b border-gray-100' : ''"
          @click="blockersExpanded = !blockersExpanded"
        >
          <div class="flex items-center gap-2">
            <span class="w-2 h-2 rounded-full bg-red-500"></span>
            <h2 class="text-sm font-semibold text-gray-900">Blockers</h2>
            <span v-if="blockers.length" class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
              {{ blockers.length }}
            </span>
            <span v-if="activeBlockerCount" class="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
              {{ activeBlockerCount }} active
            </span>
          </div>
          <div class="flex items-center gap-2">
            <span v-if="persistingCount" class="text-xs text-gray-400">{{ persistingCount }} persisting &gt;20 days</span>
            <font-awesome-icon
              :icon="['fas', blockersExpanded ? 'chevron-up' : 'chevron-down']"
              class="w-3 h-3 text-gray-400 transition-transform"
            />
          </div>
        </header>

        <!-- Collapsed preview: top 3 blockers as bullet list -->
        <div v-if="!blockersExpanded && blockers.length > 0" class="px-4 py-3 space-y-1.5">
          <div v-for="b in topBlockers" :key="b.blocker_id" class="flex items-start gap-2 text-xs">
            <span class="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0 mt-1"></span>
            <span class="text-gray-700">{{ b.description }}</span>
          </div>
          <p v-if="blockers.length > 3" class="text-xs text-blue-600 cursor-pointer hover:text-blue-800" @click.stop="blockersExpanded = true">
            + {{ blockers.length - 3 }} more
          </p>
        </div>

        <!-- Expanded: full blocker detail -->
        <div v-if="blockersExpanded" class="p-4">
          <div v-if="blockers.length === 0" class="text-sm text-gray-400 py-6 text-center">No active blockers.</div>
          <div v-else class="space-y-4">
            <div v-for="bg in blockerGroups" :key="bg.first_seen_date">
              <div class="flex items-center gap-2 mb-2">
                <div class="w-2 h-2 rounded-full bg-gray-300"></div>
                <span class="text-xs font-medium text-gray-500">{{ formatDate(bg.first_seen_date) }}</span>
              </div>
              <div class="space-y-2 pl-4 border-l border-gray-200">
                <template v-for="b in visibleBlockers(bg)" :key="b.blocker_id">
                  <div class="rounded border transition-colors" :class="b.is_resolved ? 'border-emerald-100 bg-emerald-50/30 hover:bg-emerald-50/50' : 'border-red-100 bg-red-50/30 hover:bg-red-50/50'">
                    <div class="p-3">
                      <div class="flex items-start justify-between gap-2">
                        <p class="text-sm flex-1 leading-snug" :class="b.is_resolved ? 'text-gray-500 line-through' : 'text-gray-900'">{{ b.description }}</p>
                        <button
                          v-if="!b.is_resolved"
                          class="shrink-0 w-6 h-6 flex items-center justify-center rounded border transition-colors"
                          :class="isInAgenda('blocker', b.blocker_id)
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-600 cursor-default'
                            : 'border-gray-200 text-gray-400 hover:border-red-300 hover:text-red-600 hover:bg-red-50'"
                          :title="isInAgenda('blocker', b.blocker_id) ? 'Added to agenda' : 'Add to agenda'"
                          @click.stop="addToAgenda('blocker', b.blocker_id, b.description)"
                        >
                          <font-awesome-icon :icon="['fas', isInAgenda('blocker', b.blocker_id) ? 'check' : 'plus']" class="w-2.5 h-2.5" />
                        </button>
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium shrink-0" :class="blockerBadgeClass(b)">
                          {{ blockerBadgeLabel(b) }}
                        </span>
                      </div>
                      <p v-if="b.metadata_context" class="text-xs text-gray-500 mt-1.5 leading-relaxed">{{ b.metadata_context }}</p>
                      <div class="flex items-center justify-between mt-2">
                        <span v-if="b.category" class="text-xs text-gray-500">{{ b.category }}</span>
                        <span v-else></span>
                        <button class="text-xs text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline" @click.stop="toggle('blocker', b.blocker_id)">
                          → {{ isExpanded('blocker', b.blocker_id) ? 'Hide details' : 'View details' }}
                        </button>
                      </div>
                      <p v-if="b.resolution_note" class="mt-1.5 text-xs text-emerald-700 italic">✓ {{ b.resolution_note }}</p>
                      <p v-else-if="b.progress_note" class="mt-1.5 text-xs text-emerald-700 italic">↗ {{ b.progress_note }}</p>
                    </div>
                    <div v-if="isExpanded('blocker', b.blocker_id)" class="border-t border-red-100 bg-white p-3 space-y-2">
                      <div>
                        <p class="text-[10px] uppercase tracking-wide text-gray-400 font-medium">First seen</p>
                        <p class="text-xs text-gray-700 mt-0.5">{{ formatDate(b.first_seen_date) }} ({{ b.days_open }} days ago)</p>
                      </div>
                      <div v-if="b.snippet_quotes && b.snippet_quotes.length">
                        <p class="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Transcript references</p>
                        <div class="mt-1 space-y-1.5">
                          <div v-for="(q, i) in b.snippet_quotes" :key="i" class="text-xs text-gray-600 border-l-2 border-gray-200 pl-2">
                            <span class="text-gray-400">{{ formatDate(q.date) }} —</span> "{{ q.quote }}"
                          </div>
                        </div>
                      </div>
                      <div v-if="linkedActionItemsForBlocker(b.blocker_id).length">
                        <p class="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Linked action items</p>
                        <ul class="mt-0.5 space-y-0.5">
                          <li v-for="a in linkedActionItemsForBlocker(b.blocker_id)" :key="a.action_id" class="text-xs text-gray-700">• {{ a.title }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </template>
                <button
                  v-if="hiddenBlockerCount(bg) > 0"
                  class="text-xs text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline"
                  @click="toggle('blockergroup', bg.first_seen_date)"
                >
                  + {{ hiddenBlockerCount(bg) }} more
                </button>
                <button
                  v-else-if="isExpanded('blockergroup', bg.first_seen_date) && bg.blockers.length > GROUP_LIMIT"
                  class="text-xs text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline"
                  @click="toggle('blockergroup', bg.first_seen_date)"
                >
                  Show less
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>

    <!-- TERTIARY: Action Items (collapsed accordion) -->
    <section class="rounded-lg border border-gray-200 bg-white">
      <header
        class="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
        :class="actionItemsExpanded ? 'border-b border-gray-100' : ''"
        @click="actionItemsExpanded = !actionItemsExpanded"
      >
        <div class="flex items-center gap-2">
          <span class="w-2 h-2 rounded-full bg-blue-500"></span>
          <h2 class="text-sm font-semibold text-gray-900">Action Items</h2>
          <span v-if="actionItemCount" class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
            {{ actionItemCount }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <span v-if="actionItemCount" class="text-xs font-medium text-gray-600">{{ actionStatusSummary }}</span>
          <font-awesome-icon
            :icon="['fas', actionItemsExpanded ? 'chevron-up' : 'chevron-down']"
            class="w-3 h-3 text-gray-400 transition-transform"
          />
        </div>
      </header>

      <div v-if="actionItemsExpanded" class="p-4">
        <div v-if="actionGroups.length === 0" class="text-sm text-gray-400 py-6 text-center">
          No action items from the last {{ header.engagement_count_summarized || 3 }} meetings.
        </div>
        <div v-else class="space-y-4">
          <div v-for="group in actionGroups" :key="group.engagement_id">
            <div class="flex items-center gap-2 mb-2">
              <div class="w-2 h-2 rounded-full bg-gray-300"></div>
              <span class="text-xs font-medium text-gray-500">{{ formatDate(group.engagement_date) }}</span>
            </div>
            <div class="space-y-2 pl-4 border-l border-gray-200">
              <template v-for="item in visibleItems(group)" :key="item.action_id">
                <div class="rounded border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                  <div class="p-3">
                    <div class="flex items-start justify-between gap-2">
                      <p class="text-sm text-gray-900 flex-1 leading-snug">{{ item.title }}</p>
                      <div class="flex items-center gap-1 shrink-0">
                        <button
                          class="w-6 h-6 flex items-center justify-center rounded border transition-colors"
                          :class="isInAgenda('action_item', item.action_id)
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-600 cursor-default'
                            : 'border-gray-200 text-gray-400 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50'"
                          :title="isInAgenda('action_item', item.action_id) ? 'Added to agenda' : 'Add to agenda'"
                          @click.stop="addToAgenda('action_item', item.action_id, item.title)"
                        >
                          <font-awesome-icon :icon="['fas', isInAgenda('action_item', item.action_id) ? 'check' : 'plus']" class="w-2.5 h-2.5" />
                        </button>
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" :class="statusBadgeClass(item.status)">
                          <span>{{ statusIcon(item.status) }}</span>
                          {{ statusLabel(item.status) }}
                        </span>
                        <span v-if="isInferredStatus(item)" class="text-[10px] text-gray-400 italic">(inferred)</span>
                        <span v-if="item.is_overdue" class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
                          Overdue {{ item.days_overdue ? item.days_overdue + 'd' : '' }}
                        </span>
                      </div>
                    </div>
                    <div class="flex items-center justify-between mt-2">
                      <div class="flex items-center gap-3 text-xs text-gray-500">
                        <div v-if="item.owner_name" class="flex items-center gap-1.5">
                          <span class="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-medium" :class="ownerAvatarClass(item.owner_kind)">
                            {{ ownerInitial(item.owner_name) }}
                          </span>
                          <span>{{ item.owner_name }}</span>
                        </div>
                        <span v-if="item.due_date" class="text-xs text-gray-400">
                          <i class="fa-regular fa-clock mr-0.5"></i>Due {{ formatDate(item.due_date) }}
                        </span>
                      </div>
                      <button class="text-xs text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline" @click.stop="toggle('action', item.action_id)">
                        → {{ isExpanded('action', item.action_id) ? 'Hide details' : 'View details' }}
                      </button>
                    </div>
                    <p v-if="item.evidence" class="mt-1.5 text-xs text-gray-500 italic">{{ item.evidence }}</p>
                  </div>
                  <div v-if="isExpanded('action', item.action_id)" class="border-t border-gray-100 bg-white p-3 space-y-2">
                    <div v-if="item.description">
                      <p class="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Full description</p>
                      <p class="text-xs text-gray-700 mt-0.5">{{ item.description }}</p>
                    </div>
                    <div v-if="item.snippet_quotes && item.snippet_quotes.length">
                      <p class="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Transcript references</p>
                      <div class="mt-1 space-y-1.5">
                        <div v-for="(q, i) in item.snippet_quotes" :key="i" class="text-xs text-gray-600 border-l-2 border-gray-200 pl-2">
                          <span class="text-gray-400">{{ formatDate(q.date) }} —</span> "{{ q.quote }}"
                        </div>
                      </div>
                    </div>
                    <div v-if="item.related_blocker_id">
                      <p class="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Linked blocker</p>
                      <p class="text-xs text-gray-700 mt-0.5">{{ relatedBlockerDescription(item.related_blocker_id) }}</p>
                    </div>
                  </div>
                </div>
              </template>
              <button
                v-if="hiddenCount(group) > 0"
                class="text-xs text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline"
                @click="toggle('group', group.engagement_id)"
              >
                + {{ hiddenCount(group) }} more
              </button>
              <button
                v-else-if="isExpanded('group', group.engagement_id) && group.items.length > GROUP_LIMIT"
                class="text-xs text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline"
                @click="toggle('group', group.engagement_id)"
              >
                Show less
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- OTHER: Open action items not tied to transcribed meetings -->
    <section v-if="otherActionItems.length" class="rounded-lg border border-gray-200 bg-white">
      <header
        class="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
        :class="otherItemsExpanded ? 'border-b border-gray-100' : ''"
        @click="otherItemsExpanded = !otherItemsExpanded"
      >
        <div class="flex items-center gap-2">
          <span class="w-2 h-2 rounded-full bg-gray-400"></span>
          <h2 class="text-sm font-semibold text-gray-900">Other open action items</h2>
          <span class="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
            {{ otherActionItems.length }}
          </span>
        </div>
        <font-awesome-icon
          :icon="['fas', otherItemsExpanded ? 'chevron-up' : 'chevron-down']"
          class="w-3 h-3 text-gray-400 transition-transform"
        />
      </header>

      <div v-if="otherItemsExpanded" class="p-4">
        <div class="space-y-2">
          <div v-for="item in sortedOtherItems" :key="item.action_id" class="rounded border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
            <div class="p-3">
              <div class="flex items-start justify-between gap-2">
                <p class="text-sm text-gray-900 flex-1 leading-snug">{{ item.title }}</p>
                <div class="flex items-center gap-1 shrink-0">
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" :class="statusBadgeClass(item.status)">
                    <span>{{ statusIcon(item.status) }}</span>
                    {{ statusLabel(item.status) }}
                  </span>
                  <span v-if="item.is_overdue" class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
                    Overdue {{ item.days_overdue ? item.days_overdue + 'd' : '' }}
                  </span>
                </div>
              </div>
              <div class="flex items-center gap-3 mt-2 text-xs text-gray-500">
                <div v-if="item.owner_name" class="flex items-center gap-1.5">
                  <span class="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-medium" :class="ownerAvatarClass(item.owner_kind)">
                    {{ ownerInitial(item.owner_name) }}
                  </span>
                  <span>{{ item.owner_name }}</span>
                </div>
                <span v-if="item.due_date" class="text-xs text-gray-400">
                  <i class="fa-regular fa-clock mr-0.5"></i>Due {{ formatDate(item.due_date) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
const STATUS_LABELS = { done: 'Done', missed: 'Missed', in_progress: 'In progress', new: 'New' }
const STATUS_ICONS = { done: '✓', missed: '×', in_progress: '⊙', new: '+' }
const STATUS_BADGE = {
  done: 'bg-emerald-100 text-emerald-800',
  missed: 'bg-red-100 text-red-800',
  in_progress: 'bg-amber-100 text-amber-800',
  new: 'bg-purple-100 text-purple-800',
}
const SOURCE_CHIP = {
  action_item: 'bg-red-50 text-red-700 border border-red-100',
  blocker: 'bg-amber-50 text-amber-700 border border-amber-100',
  metric: 'bg-blue-50 text-blue-700 border border-blue-100',
  topic: 'bg-gray-100 text-gray-700 border border-gray-200',
}
const GROUP_LIMIT = 3

export default {
  name: 'BriefView',
  props: {
    responseData: { type: Object, required: true },
    agendaChecker: { type: Function, default: null },
  },
  emits: ['add-to-agenda'],
  data() {
    return {
      expanded: {},
      GROUP_LIMIT,
      blockersExpanded: false,
      actionItemsExpanded: false,
      otherItemsExpanded: false,
    }
  },
  computed: {
    header() {
      return this.responseData.header || {}
    },
    nextMeetingLine() {
      const m = this.header.next_meeting
      if (!m) return null
      const dt = new Date(m.start_datetime)
      const day = dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
      const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      let line = `Next meeting: ${day} at ${time}`
      if (m.subject) line += ` — ${m.subject}`
      if (m.host) line += ` (${m.host})`
      return line
    },
    actionGroups() {
      return this.responseData.action_items || []
    },
    actionItemCount() {
      return this.actionGroups.reduce((n, g) => n + (g.items?.length || 0), 0)
    },
    blockers() {
      return this.responseData.blockers || []
    },
    blockerGroups() {
      const groups = {}
      for (const b of this.blockers) {
        const date = b.first_seen_date || 'unknown'
        if (!groups[date]) groups[date] = { first_seen_date: date, blockers: [] }
        groups[date].blockers.push(b)
      }
      for (const g of Object.values(groups)) {
        g.blockers.sort((a, b) => Number(!!a.is_resolved) - Number(!!b.is_resolved))
      }
      return Object.values(groups).sort((a, b) => (b.first_seen_date || '').localeCompare(a.first_seen_date || ''))
    },
    persistingCount() {
      return this.blockers.filter(b => (b.days_open || 0) > 20).length
    },
    activeBlockerCount() {
      return this.blockers.filter(b => !b.is_resolved).length
    },
    topBlockers() {
      return this.blockers.filter(b => !b.is_resolved).slice(0, 3)
    },
    actionStatusSummary() {
      let done = 0
      let total = 0
      const meetings = this.actionGroups.length
      for (const g of this.actionGroups) {
        for (const item of g.items || []) {
          total++
          if (item.status === 'done') done++
        }
      }
      const open = total - done
      if (!total) return ''
      const meetingStr = meetings === 1 ? '1 meeting' : `${meetings} meetings`
      return `${total} action items across ${meetingStr} — ${done} complete, ${open} open`
    },
    sinceItems() {
      return this.responseData.whats_happened_since?.items || []
    },
    sinceRangeLabel() {
      const since = this.responseData.whats_happened_since?.since_date
      const until = this.responseData.whats_happened_since?.until_date
      if (!since || !until) return ''
      const days = Math.round((new Date(until) - new Date(since)) / 86400000)
      return `${this.formatDate(since)} → ${this.formatDate(until)} · ${days} days`
    },
    talkingPoints() {
      return this.responseData.talking_points || []
    },
    recentDisruptions() {
      return this.header.recent_disruptions || []
    },
    disruptionsSummary() {
      const items = this.recentDisruptions
      if (!items.length) return ''
      const counts = {}
      for (const d of items) {
        const s = (d.status || 'disruption').toLowerCase()
        counts[s] = (counts[s] || 0) + 1
      }
      const parts = Object.entries(counts).map(
        ([s, c]) => `${c} ${s}${c > 1 ? 's' : ''}`,
      )
      return parts.join(' and ') + ' in the last 90 days'
    },
    disruptionsDetail() {
      return this.recentDisruptions
        .map(
          (d) =>
            `${d.status}: ${this.formatDate(d.date)}${d.subject ? ' — ' + d.subject : ''}`,
        )
        .join(' · ')
    },
    otherActionItems() {
      return this.responseData.other_action_items || []
    },
    sortedOtherItems() {
      return [...this.otherActionItems].sort(
        (a, b) => Number(!!b.is_overdue) - Number(!!a.is_overdue),
      )
    },
    blockersById() {
      const map = {}
      for (const b of this.blockers) map[b.blocker_id] = b
      return map
    },
    actionsByBlockerId() {
      const map = {}
      for (const g of this.actionGroups) {
        for (const a of g.items || []) {
          if (!a.related_blocker_id) continue
          if (!map[a.related_blocker_id]) map[a.related_blocker_id] = []
          map[a.related_blocker_id].push(a)
        }
      }
      return map
    },
  },
  methods: {
    isExpanded(kind, id) {
      return !!this.expanded[`${kind}:${id}`]
    },
    toggle(kind, id) {
      const key = `${kind}:${id}`
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] }
    },
    visibleItems(group) {
      const items = [...(group.items || [])].sort(
        (a, b) => Number(!!b.is_overdue) - Number(!!a.is_overdue),
      )
      if (this.isExpanded('group', group.engagement_id)) return items
      return items.slice(0, GROUP_LIMIT)
    },
    hiddenCount(group) {
      const items = group.items || []
      if (this.isExpanded('group', group.engagement_id)) return 0
      return Math.max(0, items.length - GROUP_LIMIT)
    },
    visibleBlockers(bg) {
      const items = bg.blockers || []
      if (this.isExpanded('blockergroup', bg.first_seen_date)) return items
      return items.slice(0, GROUP_LIMIT)
    },
    hiddenBlockerCount(bg) {
      const items = bg.blockers || []
      if (this.isExpanded('blockergroup', bg.first_seen_date)) return 0
      return Math.max(0, items.length - GROUP_LIMIT)
    },
    formatDate(d) {
      if (!d) return ''
      const date = new Date(d)
      if (isNaN(date)) return d
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    statusLabel(s) { return STATUS_LABELS[s] || s },
    statusIcon(s) { return STATUS_ICONS[s] || '' },
    statusBadgeClass(s) { return STATUS_BADGE[s] || 'bg-gray-100 text-gray-700' },
    ownerInitial(name) {
      if (!name) return '?'
      return name.trim().charAt(0).toUpperCase()
    },
    ownerAvatarClass(kind) {
      return kind === 'tenant' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'
    },
    isInferredStatus(item) {
      return (item.status === 'done' || item.status === 'in_progress') && item.status_source === 'transcript'
    },
    blockerBadgeClass(b) {
      if (b.is_resolved) return 'bg-emerald-100 text-emerald-800'
      if (b.is_new) return 'bg-purple-100 text-purple-800'
      return 'bg-amber-100 text-amber-800'
    },
    blockerBadgeLabel(b) {
      if (b.is_resolved) return '✓ Resolved'
      if (b.is_new) return '+ New'
      return '⚠ Blocker'
    },
    directionIcon(d) {
      return { up: '↑', down: '↓', warn: '!', info: '→' }[d] || '→'
    },
    directionBgClass(d) {
      return {
        up: 'bg-emerald-50',
        down: 'bg-red-50',
        warn: 'bg-red-50',
        info: 'bg-gray-100',
      }[d] || 'bg-gray-100'
    },
    directionTextClass(d) {
      return {
        up: 'text-emerald-700',
        down: 'text-red-700',
        warn: 'text-red-700',
        info: 'text-gray-600',
      }[d] || 'text-gray-600'
    },
    trendIcon(trend) {
      return { improving: '↗', declining: '↘', flat: '→' }[trend] || ''
    },
    trendClass(trend) {
      return (
        {
          improving: 'text-emerald-600',
          declining: 'text-red-600',
          flat: 'text-gray-400',
        }[trend] || ''
      )
    },
    sourceChipClass(kind) {
      return SOURCE_CHIP[kind] || SOURCE_CHIP.topic
    },
    relatedBlockerDescription(id) {
      return this.blockersById[id]?.description || `Blocker #${id}`
    },
    linkedActionItemsForBlocker(id) {
      return this.actionsByBlockerId[id] || []
    },
    hasTalkingPointDetail(tp) {
      return this.talkingPointSources(tp).length > 0
    },
    talkingPointSources(tp) {
      const ids = tp.source_ids || {}
      const toArr = v => (v == null ? [] : (Array.isArray(v) ? v : [v]))
      const out = []
      for (const id of toArr(ids.action_id)) {
        const a = this.findAction(id)
        if (a) out.push({ kind: 'action', title: a.title, description: a.description })
      }
      for (const id of toArr(ids.blocker_id)) {
        const b = this.blockersById[id]
        if (b) out.push({ kind: 'blocker', title: b.description, description: null })
      }
      return out
    },
    findAction(id) {
      for (const g of this.actionGroups) {
        for (const a of g.items || []) if (a.action_id === id) return a
      }
      return null
    },
    addToAgenda(origin, sourceId, text) {
      if (this.isInAgenda(origin, sourceId)) return
      this.$emit('add-to-agenda', { origin, sourceId, text })
    },
    isInAgenda(origin, sourceId) {
      if (!this.agendaChecker) return false
      return this.agendaChecker(origin, sourceId)
    },
  },
}
</script>
